import React, { useMemo, useState } from 'react';

const EditModeDiffPanel = ({
  pendingEdits,
  originalData,
  amendmentNotes,
  onNotesChange,
  onSave,
  onDiscard,
  onUndoEdit,
  isSaving,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const groupedEdits = useMemo(() => {
    if (!pendingEdits || pendingEdits.size === 0)
      return { byTicket: new Map(), byField: new Map(), totalEdits: 0 };

    const byTicket = new Map();
    const byField = new Map();
    let totalEdits = 0;

    for (const [key, newValue] of pendingEdits) {
      const [ticketUID, field] = key.split('::');
      const origRow = originalData?.find((r) => r.TicketUID === ticketUID);
      const oldValue = origRow ? String(origRow[field] ?? '') : '';

      totalEdits++;

      if (!byTicket.has(ticketUID)) {
        byTicket.set(ticketUID, {
          ticketNo: origRow?.['Ticket No'] || ticketUID.slice(0, 8),
          edits: [],
        });
      }
      byTicket.get(ticketUID).edits.push({ field, oldValue, newValue });

      if (!byField.has(field)) {
        byField.set(field, []);
      }
      byField
        .get(field)
        .push({
          ticketUID,
          ticketNo: origRow?.['Ticket No'] || ticketUID.slice(0, 8),
          oldValue,
          newValue,
        });
    }

    return { byTicket, byField, totalEdits };
  }, [pendingEdits, originalData]);

  const { byField, totalEdits } = groupedEdits;

  if (totalEdits === 0) return null;

  const ticketCount = groupedEdits.byTicket.size;
  const canSave = totalEdits > 0 && amendmentNotes.trim().length > 0;

  return (
    <div className="border-t border-amber-200 bg-amber-50/60">
      <div
        className="flex items-center justify-between px-4 py-2.5 cursor-pointer hover:bg-amber-100/50 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2.5">
          <svg
            className={`w-3.5 h-3.5 text-amber-500 transition-transform duration-150 ${isExpanded ? 'rotate-90' : ''}`}
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
              clipRule="evenodd"
            />
          </svg>
          <span className="text-xs font-semibold text-amber-700 uppercase tracking-wider">
            Pending Changes
          </span>
          <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold rounded-full bg-amber-200 text-amber-800 tabular-nums">
            {totalEdits} edit{totalEdits !== 1 ? 's' : ''} across {ticketCount} ticket
            {ticketCount !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDiscard();
            }}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md text-red-600 bg-surface border border-red-200 hover:bg-red-50 transition-colors"
          >
            <svg
              className="w-3 h-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
            Discard All
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onSave();
            }}
            disabled={!canSave || isSaving}
            className="inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-sm"
          >
            {isSaving ? (
              <>
                <svg className="animate-spin w-3 h-3" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                Saving...
              </>
            ) : (
              <>
                <svg
                  className="w-3 h-3"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                Save All Changes
              </>
            )}
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="px-4 pb-4 space-y-3">
          {/* Amendment Notes */}
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-[0.14em] text-amber-600 mb-1 block">
              Amendment Notes <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={amendmentNotes}
              onChange={(e) => onNotesChange(e.target.value)}
              placeholder="Reason for these changes..."
              className={`w-full px-3 py-1.5 text-sm border rounded-md outline-none transition-colors ${
                !amendmentNotes.trim()
                  ? 'border-red-300 bg-red-50/50 focus:ring-1 focus:ring-red-400 focus:border-red-400'
                  : 'border-amber-300 bg-surface focus:ring-1 focus:ring-primary-400 focus:border-primary-400'
              }`}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* Grouped by field */}
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {Array.from(byField.entries()).map(([field, edits]) => (
              <div
                key={field}
                className="rounded-md border border-amber-200 bg-surface overflow-hidden"
              >
                <div className="flex items-center justify-between px-3 py-1.5 bg-amber-50/80 border-b border-amber-100">
                  <span className="text-xs font-medium text-amber-800">{field}</span>
                  <span className="text-[10px] text-amber-500 tabular-nums">
                    {edits.length} change{edits.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="divide-y divide-amber-50">
                  {edits.slice(0, 10).map((edit) => (
                    <div
                      key={edit.ticketUID}
                      className="flex items-center gap-2 px-3 py-1 text-xs group"
                    >
                      <span
                        className="font-mono text-[10px] text-slate-500 w-24 flex-shrink-0 truncate"
                        title={edit.ticketUID}
                      >
                        {edit.ticketNo}
                      </span>
                      <span
                        className="text-red-500 line-through truncate max-w-[120px]"
                        title={edit.oldValue}
                      >
                        {edit.oldValue || '(blank)'}
                      </span>
                      <svg
                        className="w-3 h-3 text-slate-300 flex-shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M13 7l5 5m0 0l-5 5m5-5H6"
                        />
                      </svg>
                      <span
                        className="text-emerald-600 font-medium truncate max-w-[120px]"
                        title={edit.newValue}
                      >
                        {edit.newValue || '(blank)'}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onUndoEdit(edit.ticketUID, field);
                        }}
                        className="ml-auto opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500 p-0.5 rounded transition-all"
                        title="Undo this edit"
                      >
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M3 10h10a5 5 0 015 5v2M3 10l4-4m-4 4l4 4"
                          />
                        </svg>
                      </button>
                    </div>
                  ))}
                  {edits.length > 10 && (
                    <div className="px-3 py-1 text-[10px] text-amber-500 italic">
                      +{edits.length - 10} more changes
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EditModeDiffPanel;
